import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Switch from "../common/Switch";
import { updateSubAccountPermissions } from "../../services/subAccountService";
import { color } from "../../styles/theme";

const PERMISSION_KEYS = [
  "canManageEvents", "canManageRooms", "canManageAccessQrCheckIn", "canManageLocationExceptions", "canManageGallery",
  "canManageSingleMedia", "canManagePricing", "canManageWallet", "canManageSupport", "canManageSubAccounts",
];
const toApiKey = (key) => key.charAt(0).toUpperCase() + key.slice(1);

/** @param subAccount a row from getMySubAccounts (SubAccountId + Permissions PascalCase object) */
const EditSubAccountPermissionsModal = ({ subAccount, onClose, onUpdated }) => {
  const { t } = useTranslation(["subAccounts", "common"]);
  const initial = useMemo(
    () => PERMISSION_KEYS.reduce((acc, k) => ({ ...acc, [k]: Boolean(subAccount?.Permissions?.[toApiKey(k)]) }), {}),
    [subAccount]
  );
  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);

  const hasChanges = PERMISSION_KEYS.some((k) => values[k] !== initial[k]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = PERMISSION_KEYS.reduce((acc, k) => ({ ...acc, [toApiKey(k)]: values[k] }), {});
      const res = await updateSubAccountPermissions(subAccount.SubAccountId, payload);
      toast.success(res.data?.Message || t("editPermissionsModal.success"));
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("editPermissionsModal.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("editPermissionsModal.title")}
      onClose={onClose}
      busy={saving}
      maxWidth={420}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>{t("common:actions.cancel")}</Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>{saving ? t("editPermissionsModal.saving") : t("common:actions.save")}</Button>
        </>
      }
    >
      <div style={s.list}>
        {PERMISSION_KEYS.map((key, i) => (
          <div key={key} style={{ ...s.row, ...(i === PERMISSION_KEYS.length - 1 ? { borderBottom: "none" } : {}) }}>
            <span style={s.rowLabel}>{t(`permissions.${key}`)}</span>
            <Switch checked={values[key]} onChange={() => setValues((prev) => ({ ...prev, [key]: !prev[key] }))} disabled={saving} />
          </div>
        ))}
      </div>
    </Modal>
  );
};

const s = {
  list: { display: "flex", flexDirection: "column" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 2px", borderBottom: `1px solid ${color.gray100}` },
  rowLabel: { fontSize: 13.5, color: color.gray800, fontWeight: 500 },
};

export default EditSubAccountPermissionsModal;
